/* eslint-disable react-refresh/only-export-components -- co-located helper; HMR-only warning */
import { ArrowUpDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getSPTSLabel, type ScoringMode } from '../playersMobileStats';

export type MobileSortKey = 'SPTS' | 'GP' | 'Name';
export type SortDir = 'asc' | 'desc';

interface SortMenuProps {
  sortKey: MobileSortKey;
  sortDir: SortDir;
  scoringMode: ScoringMode;
  onSortKeyChange: (key: MobileSortKey) => void;
  onSortDirChange: (dir: SortDir) => void;
}

export function getSortLabel(key: MobileSortKey, scoringMode: ScoringMode): string {
  if (key === 'SPTS') return getSPTSLabel(scoringMode);
  if (key === 'GP') return 'Games played';
  return 'Name';
}

export function SortMenu({
  sortKey,
  sortDir,
  scoringMode,
  onSortKeyChange,
  onSortDirChange,
}: SortMenuProps) {
  const items: { key: MobileSortKey; label: string }[] = [
    { key: 'SPTS', label: getSPTSLabel(scoringMode) },
    { key: 'GP', label: 'Games played' },
    { key: 'Name', label: 'Name (A-Z)' },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          aria-label="Sort"
        >
          <ArrowUpDown className="w-3 h-3" />
          <span>Sort: {getSortLabel(sortKey, scoringMode)}</span>
          <span aria-hidden="true">{sortDir === 'desc' ? '↓' : '↑'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.key}
            onClick={() => onSortKeyChange(it.key)}
            className="flex items-center justify-between"
          >
            <span className={cn(it.key === sortKey && 'text-primary font-semibold')}>
              {it.label}
            </span>
            {it.key === sortKey && <Check className="w-3.5 h-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onSortDirChange(sortDir === 'desc' ? 'asc' : 'desc')}
          className="text-xs text-muted-foreground"
        >
          Direction: {sortDir === 'desc' ? 'High → Low' : 'Low → High'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SortMenu;
